/* ChartOfAccountsTab — CRUD for the Chart of Accounts.
 * Codes are grouped by type (revenue / expense). System-seeded codes show a
 * lock badge and cannot be hard-deleted; they can still be deactivated.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BookOpen, Plus, Loader2, Edit3, Save, X, Lock, Power, PowerOff, RefreshCw,
} from 'lucide-react';
import { invalidateAccountsCache } from '../../../components/admin/AccountCodePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACCT_TYPE_BADGE = {
  asset: 'bg-blue-50 text-blue-800 border-blue-200',
  liability: 'bg-purple-50 text-purple-800 border-purple-200',
  net_assets: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  revenue: 'bg-green-50 text-green-800 border-green-200',
  expense: 'bg-amber-50 text-amber-800 border-amber-200',
};

const FUNCTIONAL_BADGE = {
  program: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  administrative: 'bg-slate-50 text-slate-700 border-slate-200',
  fundraising: 'bg-rose-50 text-rose-800 border-rose-200',
};

// Seven nonprofit-standard groupings (FASB ASC 958 / IRS Form 990 aligned).
// Each entry decides which codes appear in which bucket using a matcher.
const NONPROFIT_GROUPS = [
  { id: 'asset', label: 'Assets (1xxx)',
    sub: 'What NHF owns — cash, pledges receivable, inventory.',
    match: (a) => a.type === 'asset' },
  { id: 'liability', label: 'Liabilities (2xxx)',
    sub: 'What NHF owes — payables, accrued expenses, donor-restricted balances.',
    match: (a) => a.type === 'liability' },
  { id: 'net_assets', label: 'Net Assets / Equity (3xxx)',
    sub: "NHF's accumulated mission capital — unrestricted vs. donor-restricted.",
    match: (a) => a.type === 'net_assets' },
  { id: 'revenue', label: 'Revenue (4xxx)',
    sub: 'Donations, grants, sponsorships, merch & event revenue.',
    match: (a) => a.type === 'revenue' },
  { id: 'program', label: 'Program Expenses (5xxx)',
    sub: 'Direct mission delivery — the numbers donors and grant officers focus on.',
    match: (a) => a.type === 'expense' && a.functional_class === 'program' },
  { id: 'administrative', label: 'Administrative Expenses (6xxx)',
    sub: 'General management and overhead — keep this lean.',
    match: (a) => a.type === 'expense' && a.functional_class === 'administrative' },
  { id: 'fundraising', label: 'Fundraising Expenses (7xxx)',
    sub: 'Cost of raising money — events, campaigns, donor cultivation.',
    match: (a) => a.type === 'expense' && a.functional_class === 'fundraising' },
];

const ChartOfAccountsTab = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', notes: '', active: true });
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ code: '', name: '', type: 'expense', kind: 'manual', functional_class: 'program', notes: '', active: true });
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/chart-of-accounts?include_inactive=${showInactive}`,
        { withCredentials: true });
      setAccounts(r.data?.accounts || []);
      invalidateAccountsCache();
    } catch (e) { setError(e.response?.data?.detail || 'Could not load chart of accounts.'); }
    finally { setLoading(false); }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const runMigrate = async () => {
    if (!window.confirm('Backfill account codes on every legacy expense? Safe to run multiple times.')) return;
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/chart-of-accounts/migrate`,
        {}, { withCredentials: true });
      alert(`Migrated ${r.data.expenses_updated} expenses.\n${Object.keys(r.data.unmapped_categories || {}).length ? 'Unmapped categories: ' + JSON.stringify(r.data.unmapped_categories) : 'All categories mapped cleanly.'}`);
    } catch (e) { alert(e.response?.data?.detail || 'Migration failed.'); }
  };

  const runUpgrade = async () => {
    if (!window.confirm('Apply Chart of Accounts v2 upgrade?\n\nThis adds any missing Asset / Liability / Net-Assets / Fundraising codes, syncs functional class on system codes, and remaps any expenses still using retired codes. Safe to run multiple times.')) return;
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/chart-of-accounts/upgrade-v2`,
        {}, { withCredentials: true });
      alert(`Upgrade complete.\n• ${r.data.new_codes_inserted} new codes inserted\n• ${r.data.system_codes_synced} system codes synced\n• ${r.data.expenses_remapped} expenses remapped from retired codes\n• ${r.data.retired_codes_deactivated} retired codes hidden`);
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Upgrade failed.'); }
  };

  const saveEdit = async (code) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/chart-of-accounts/${code}`,
        editForm, { withCredentials: true });
      setEditingCode(null);
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed.'); }
  };

  const toggleActive = async (acct) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/chart-of-accounts/${acct.code}`,
        { active: !acct.active }, { withCredentials: true });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Toggle failed.'); }
  };

  const createNew = async () => {
    if (!/^\d{2,5}$/.test(newForm.code)) { alert('Code must be 2-5 digits.'); return; }
    if (!newForm.name.trim()) { alert('Name is required.'); return; }
    // Functional class only applies to expenses
    const payload = { ...newForm, name: newForm.name.trim() };
    if (newForm.type !== 'expense') payload.functional_class = null;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/chart-of-accounts`,
        payload, { withCredentials: true });
      setShowNew(false);
      setNewForm({ code: '', name: '', type: 'expense', kind: 'manual', functional_class: 'program', notes: '', active: true });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Create failed.'); }
  };

  // Group accounts into the seven nonprofit-standard buckets.
  const grouped = NONPROFIT_GROUPS.map((g) => ({
    ...g,
    accounts: accounts.filter(g.match).sort((a, b) => a.code.localeCompare(b.code)),
  }));
  const unclassifiedExpenses = accounts.filter(
    (a) => a.type === 'expense' && !a.functional_class,
  );

  return (
    <div className="space-y-4" data-testid="chart-of-accounts-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A0A0A] inline-flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#BA1826]" /> Chart of Accounts
          </h2>
          <p className="text-xs text-[#4B5563] mt-1 max-w-2xl">
            The 4-digit codes every money-touching transaction maps to. System-seeded codes
            are locked from deletion but can be deactivated. Add custom codes as NHF grows
            (e.g. new program lines, grant-restricted funds).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[11px] text-[#4B5563] inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
              data-testid="chart-show-inactive" /> Show inactive
          </label>
          <button onClick={load} className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5"
            data-testid="chart-refresh"><RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          <button onClick={runMigrate} className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded text-[#4B5563]"
            data-testid="chart-migrate-btn">Backfill legacy expenses</button>
          <button onClick={runUpgrade} className="text-xs px-2.5 py-1.5 border border-[#BA1826]/40 rounded text-[#BA1826] font-semibold"
            data-testid="chart-upgrade-btn">Apply v2 upgrade</button>
          <button onClick={() => setShowNew(true)}
            className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] inline-flex items-center gap-1.5"
            data-testid="chart-new-btn">
            <Plus className="w-3 h-3" /> New account
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded">{error}</div>}

      {/* New account form */}
      {showNew && (
        <div className="bg-[#FFF8F8] border border-[#BA1826]/30 rounded-lg p-3 space-y-2" data-testid="chart-new-form">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-[#BA1826]">New account code</p>
            <button onClick={() => setShowNew(false)}><X className="w-4 h-4 text-[#4B5563]" /></button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
            <input value={newForm.code} onChange={(e) => setNewForm({ ...newForm, code: e.target.value.replace(/[^\d]/g, '').slice(0, 5) })}
              placeholder="4-digit code" className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs font-mono" data-testid="new-acct-code" />
            <input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
              placeholder="Account name" className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs lg:col-span-2" data-testid="new-acct-name" />
            <select value={newForm.type}
              onChange={(e) => setNewForm({ ...newForm, type: e.target.value })}
              className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs" data-testid="new-acct-type">
              <option value="asset">Asset</option>
              <option value="liability">Liability</option>
              <option value="net_assets">Net Assets</option>
              <option value="revenue">Revenue</option>
              <option value="expense">Expense</option>
            </select>
            {newForm.type === 'expense' ? (
              <select value={newForm.functional_class}
                onChange={(e) => setNewForm({ ...newForm, functional_class: e.target.value })}
                className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs"
                data-testid="new-acct-functional">
                <option value="program">Program</option>
                <option value="administrative">Administrative</option>
                <option value="fundraising">Fundraising</option>
              </select>
            ) : <div />}
            <button onClick={createNew} className="text-xs px-3 py-1.5 bg-[#0A0A0A] text-white rounded inline-flex items-center justify-center gap-1.5"
              data-testid="new-acct-save"><Save className="w-3 h-3" /> Create</button>
          </div>
          <input value={newForm.notes} onChange={(e) => setNewForm({ ...newForm, notes: e.target.value })}
            placeholder="Notes (optional — what should be recorded here?)"
            className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-xs"
            data-testid="new-acct-notes" />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
      ) : (
        <>
          {unclassifiedExpenses.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 rounded"
              data-testid="chart-unclassified-warning">
              <p className="font-bold">{unclassifiedExpenses.length} expense {unclassifiedExpenses.length === 1 ? 'code is' : 'codes are'} missing a functional class.</p>
              <p className="mt-0.5">These won&apos;t appear under Program / Administrative / Fundraising in the Treasurer Report. Codes: {unclassifiedExpenses.map((a) => a.code).join(', ')}</p>
            </div>
          )}
          {grouped.map((g) => g.accounts.length === 0 ? null : (
            <div key={g.id} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden"
              data-testid={`chart-bucket-${g.id}`}>
              <div className="bg-[#FAFBFC] px-3 py-2 border-b border-[#E5E7EB]">
                <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">{g.label}</p>
                <p className="text-[10px] text-[#9CA3AF] mt-0.5">{g.sub}</p>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-[#F8F9FA] text-[#9CA3AF]">
                  <tr>
                    <th className="text-left px-3 py-1.5 w-20">Code</th>
                    <th className="text-left px-3 py-1.5">Name</th>
                    <th className="text-left px-3 py-1.5 hidden lg:table-cell">Notes</th>
                    <th className="text-center px-3 py-1.5 w-24">Type</th>
                    <th className="text-center px-3 py-1.5 w-28 hidden md:table-cell">Class</th>
                    <th className="text-center px-3 py-1.5 w-20">Status</th>
                    <th className="text-right px-3 py-1.5 w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {g.accounts.map((a) => editingCode === a.code ? (
                    <tr key={a.code} className="border-t border-[#E5E7EB] bg-[#FFF8F8]" data-testid={`chart-row-${a.code}-editing`}>
                      <td className="px-3 py-2 font-mono font-bold">{a.code}</td>
                      <td className="px-3 py-2"><input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs" data-testid={`edit-name-${a.code}`} /></td>
                      <td className="px-3 py-2 hidden lg:table-cell"><input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                        className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs" /></td>
                      <td colSpan={3} />
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => saveEdit(a.code)} className="text-[10px] px-2 py-1 bg-[#BA1826] text-white rounded mr-1"
                          data-testid={`save-${a.code}`}><Save className="w-3 h-3" /></button>
                        <button onClick={() => setEditingCode(null)} className="text-[10px] px-2 py-1 border border-[#E5E7EB] rounded"><X className="w-3 h-3" /></button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={a.code} className={`border-t border-[#E5E7EB] ${a.active ? '' : 'opacity-50'}`} data-testid={`chart-row-${a.code}`}>
                      <td className="px-3 py-2 font-mono font-bold">{a.code}</td>
                      <td className="px-3 py-2">
                        <span>{a.name}</span>
                        {a.is_system && <Lock className="w-3 h-3 text-[#9CA3AF] inline ml-1" title="System-seeded code (cannot be deleted)" />}
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell text-[#9CA3AF] truncate max-w-xs" title={a.notes || ''}>{a.notes || '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${ACCT_TYPE_BADGE[a.type] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>{(a.type || '').replace('_', ' ')}</span>
                      </td>
                      <td className="px-3 py-2 text-center hidden md:table-cell">
                        {a.functional_class ? (
                          <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${FUNCTIONAL_BADGE[a.functional_class] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                            {a.functional_class}
                          </span>
                        ) : <span className="text-[#9CA3AF]">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${a.active ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                          {a.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => { setEditingCode(a.code); setEditForm({ name: a.name, notes: a.notes || '', active: a.active }); }}
                          className="text-[10px] p-1 text-[#4B5563] hover:text-[#0A0A0A]" data-testid={`edit-${a.code}`}>
                          <Edit3 className="w-3 h-3" />
                        </button>
                        <button onClick={() => toggleActive(a)}
                          className={`text-[10px] p-1 ml-1 ${a.active ? 'text-amber-700' : 'text-green-700'}`}
                          title={a.active ? 'Deactivate' : 'Reactivate'} data-testid={`toggle-${a.code}`}>
                          {a.active ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default ChartOfAccountsTab;
